import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';

import { COLOR_BY_OPTIONS } from '@paragon/contract-library';
import { hasTrussDesignPermission } from '@paragon/login-tools';
import {
  COLOR_BY_LABELS,
  DimensionLabelTypeSelect,
  DimensionVisibilitySelect,
  MemberLabelOptionsSelection,
  PlateDetailSettingsComponent,
  SettingsDialogColumn,
  SettingsDialogContainer,
  SnapSettingsSelect,
} from '@paragon/user-settings';
import { objectFromKeys } from '@paragon/utilities';

import { getAppMonitor } from '../../monitor';
import { RootState } from '../../store';
import { toggleShowReferenceLines, toggleShowStandardDimensions } from '../../store/actions/visualOptionActions';
import { getActiveColorByOption } from '../../store/selectors/getColorFromMemberDefinitionSelector';
import { getEnabledColorByOptions } from '../../store/selectors/getEnabledColorByOptionsSelector';
import { isLayoutViewActive } from '../../store/selectors/layoutModeSelectors';
import {
  setColorBy,
  setDimensionLabelType,
  setMemberLabelOptions,
  setPlateDetailSettings,
  setSnapSettings,
  setVisibleDimensionCategory,
  toggleShowFieldInstalledMembers,
  toggleShowLoadsUnprojected,
  toggleShowManualLoadsByDefault,
  toggleShowPitch,
  toggleShowPlateNames,
  toggleShowPlates,
  toggleShowSelectedMemberCutAngles,
  toggleShowSelectedMemberLengthDimensions,
} from '../../store/slices/visualOptionsSlice';
import { ToolbarIconButton } from '../toolbars/top/ToolbarIconButton';

const ConnectedSnapSettingsSelect = connect(
  (state: RootState) => ({ value: state.visualOptions.snapSettings }),
  { onChange: setSnapSettings }, // this comment exists so prettier doesn't kill the vibe
)(SnapSettingsSelect);

type State = {
  open: boolean;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

export class UnconnectedViewOptions extends React.Component<Props, State> {
  state: State = {
    open: false,
  };

  onToggleColorBy = objectFromKeys(COLOR_BY_OPTIONS, (key) => () => this.props.setColorBy(key));

  onToggleShowReferenceLines = () => {
    this.props.toggleShowReferenceLines();
  };

  onToggleShowFieldInstalledMembers = () => {
    this.props.toggleShowFieldInstalledMembers();
  };

  handleClickOpen = () => {
    this.setState({ open: true });
    getAppMonitor().recordInteraction('View Options Opened');
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  toggleShowPlates = () => {
    this.props.toggleShowPlates();
  };

  toggleShowPlateNames = () => {
    this.props.toggleShowPlateNames();
  };

  toggleShowDimensions = () => {
    this.props.toggleShowDimensions();
  };

  toggleShowPitch = () => {
    this.props.toggleShowPitch();
  };

  toggleShowSelectedMemberCutAngles = () => {
    this.props.toggleShowSelectedMemberCutAngles();
  };

  toggleShowSelectedMemberLengthDimensions = () => {
    this.props.toggleShowSelectedMemberLengthDimensions();
  };

  toggleShowLoadsUnprojected = () => {
    this.props.toggleShowLoadsUnprojected();
  };

  toggleShowManualLoadsByDefault = () => {
    this.props.toggleShowManualLoadsByDefault();
  };

  render() {
    if (this.props.inLayoutMode) return null;

    return (
      <>
        <ToolbarIconButton title="View Options" icon={<RemoveRedEyeIcon />} onClick={this.handleClickOpen} />

        <Dialog
          maxWidth={false}
          open={this.state.open}
          onClose={this.handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <SettingsDialogContainer>
              <SettingsDialogColumn title="Display Options">
                {this.props.hasTrussDesignPermission && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="default"
                        onChange={this.onToggleShowReferenceLines}
                        checked={this.props.showReferenceLines}
                      />
                    }
                    label="Show Reference Lines"
                  />
                )}
                <MemberLabelOptionsSelection
                  memberLabelOptions={this.props.memberLabelOptions}
                  onChange={this.props.setMemberLabelOptions}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={this.props.showFieldInstalledMembers}
                      onChange={this.onToggleShowFieldInstalledMembers}
                    />
                  }
                  label="Show Field-Installed Members"
                />
                <FormControlLabel
                  control={
                    <Checkbox color="default" checked={this.props.showPlates} onChange={this.toggleShowPlates} />
                  }
                  label="Show Plates"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      disabled={!this.props.showPlates}
                      checked={this.props.showPlateNames}
                      onChange={this.toggleShowPlateNames}
                    />
                  }
                  label="Show Plate Names"
                />
                <PlateDetailSettingsComponent
                  value={this.props.plateDetailSettings}
                  onChange={this.props.setPlateDetailSettings}
                  disabled={!this.props.showPlates}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={this.props.showLoadsUnprojected}
                      onChange={this.toggleShowLoadsUnprojected}
                    />
                  }
                  label="Show Gravity Loads as Unprojected"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={this.props.showManualLoadsByDefault}
                      onChange={this.toggleShowManualLoadsByDefault}
                    />
                  }
                  label="Show Manual Loads When No Other Loads Visible"
                />
              </SettingsDialogColumn>

              <SettingsDialogColumn title="Color By">
                <RadioGroup
                  id="capacityColoringOptions"
                  aria-label="capacity color"
                  name="capacityColor"
                  value={this.props.colorBy}
                >
                  {this.props.colorByOptions.map((key) => (
                    <FormControlLabel
                      control={<Radio color="default" />}
                      label={COLOR_BY_LABELS[key]}
                      value={key}
                      key={key}
                      onChange={this.onToggleColorBy[key]}
                    />
                  ))}
                </RadioGroup>
              </SettingsDialogColumn>

              <SettingsDialogColumn title="Dimension Options">
                <ConnectedSnapSettingsSelect />

                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={this.props.showDimensions}
                      onChange={this.toggleShowDimensions}
                    />
                  }
                  label="Show Standard Dimensions"
                />

                <DimensionVisibilitySelect
                  value={this.props.visibleDimensionCategory}
                  onChange={this.props.setVisibleDimensionCategory}
                />

                <DimensionLabelTypeSelect
                  value={this.props.dimensionLabelType}
                  onChange={this.props.setDimensionLabelType}
                />

                <FormControlLabel
                  control={<Checkbox color="default" checked={this.props.showPitch} onChange={this.toggleShowPitch} />}
                  label="Show Pitch"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={this.props.showSelectedMemberCutAngles}
                      onChange={this.toggleShowSelectedMemberCutAngles}
                    />
                  }
                  label="Show selected member's cut angles"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={this.props.showSelectedMemberLengthDimensions}
                      onChange={this.toggleShowSelectedMemberLengthDimensions}
                    />
                  }
                  label="Show selected member's length"
                />
              </SettingsDialogColumn>
            </SettingsDialogContainer>

            <DialogActions>
              <Button
                variant="outlined"
                sx={{
                  my: 1,
                  py: 1,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    color: '#fff',
                  },
                }}
                onClick={this.handleClose}
              >
                Close
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  inLayoutMode: isLayoutViewActive(state),
  colorBy: getActiveColorByOption(state),
  showReferenceLines: state.visualOptions.showReferenceLines,
  visibleDimensionCategory: state.visualOptions.visibleDimensionCategory,
  dimensionLabelType: state.visualOptions.dimensionLabelType,
  showPitch: state.visualOptions.showPitch,
  showSelectedMemberCutAngles: state.visualOptions.showSelectedMemberCutAngles,
  showSelectedMemberLengthDimensions: state.visualOptions.showSelectedMemberLengthDimensions,
  memberLabelOptions: state.visualOptions.memberLabelOptions,
  showFieldInstalledMembers: state.visualOptions.showFieldInstalledMembers,
  showPlates: state.visualOptions.showPlates,
  showPlateNames: state.visualOptions.showPlateNames,
  plateDetailSettings: state.visualOptions.plateDetailSettings,
  hasTrussDesignPermission: hasTrussDesignPermission(state),
  colorByOptions: getEnabledColorByOptions(state),
  showDimensions: state.visualOptions.showStandardDimensions,
  showLoadsUnprojected: state.visualOptions.showLoadsUnprojected,
  showManualLoadsByDefault: state.visualOptions.showManualLoadsByDefault,
});

const actionCreators = {
  setColorBy,
  toggleShowReferenceLines,
  setMemberLabelOptions,
  toggleShowFieldInstalledMembers,
  toggleShowPlates,
  toggleShowPlateNames,
  setVisibleDimensionCategory,
  setDimensionLabelType,
  toggleShowPitch,
  toggleShowSelectedMemberCutAngles,
  toggleShowSelectedMemberLengthDimensions,
  setPlateDetailSettings,
  toggleShowDimensions: toggleShowStandardDimensions,
  toggleShowLoadsUnprojected,
  toggleShowManualLoadsByDefault,
};

export default connect(mapStateToProps, actionCreators)(UnconnectedViewOptions);
